import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/shared/lib/api";
import { useAdvisorStore } from "@/features/auth/stores/advisorStore";
import { Card, CardContent, CardHeader, CardTitle, Badge, Button } from "@/shared/components/ui";
import { StatCard, ActivityChart, TopBanksList, ConversionBars } from "@/shared/components/stats";
import { getStatusVariant, getStatusLabel, formatCurrency, formatDateTime } from "@/shared/lib/utils";
import {
  Users,
  MessageSquare,
  CreditCard,
  ArrowRight,
  Loader2,
  TrendingUp,
  Clock,
  FileText,
} from "lucide-react";
import type { Lead, Session, Offer, CreditType } from "@/shared/types";

export function DashboardPage() {
  const { advisor } = useAdvisorStore();

  const { data: leadsData, isLoading: leadsLoading } = useQuery({
    queryKey: ["leads"],
    queryFn: () => api.getLeads(),
  });

  const { data: sessionsData, isLoading: sessionsLoading } = useQuery({
    queryKey: ["sessions"],
    queryFn: () => api.getSessions(),
  });

  const leads = (leadsData?.leads || []) as Lead[];
  const sessions = (sessionsData?.sessions || []) as Session[];
  const activeSessions = sessions.filter((s) => s.status === "active");
  const completedSessions = sessions.filter((s) => s.status === "completed");

  // Calculate stats from sessions data
  const stats = useMemo(() => {
    // Count all offers from sessions
    const allOffers = sessions.flatMap((s) => (s.offers || []) as Offer[]);

    // Calculate conversion rate
    const conversionRate =
      sessions.length > 0
        ? Math.round((completedSessions.length / sessions.length) * 100)
        : 0;

    // Top banks from offers
    const bankCounts: Record<string, { count: number; totalRrso: number; rrsoCount: number }> = {};
    allOffers.forEach((offer) => {
      if (!bankCounts[offer.bankName]) {
        bankCounts[offer.bankName] = { count: 0, totalRrso: 0, rrsoCount: 0 };
      }
      bankCounts[offer.bankName].count++;
      if (offer.rrso) {
        bankCounts[offer.bankName].totalRrso += offer.rrso;
        bankCounts[offer.bankName].rrsoCount++;
      }
    });

    const topBanks = Object.entries(bankCounts)
      .map(([name, data]) => ({
        name,
        count: data.count,
        percentage: allOffers.length > 0 ? Math.round((data.count / allOffers.length) * 100) : 0,
        avgRrso: data.rrsoCount > 0 ? data.totalRrso / data.rrsoCount : undefined,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    // Conversion by credit type
    const typeStats: Record<CreditType, { leads: number; completed: number }> = {
      hipoteczny: { leads: 0, completed: 0 },
      gotowkowy: { leads: 0, completed: 0 },
      konsolidacyjny: { leads: 0, completed: 0 },
    };

    sessions.forEach((session) => {
      const type = (session.lead?.creditType || "gotowkowy") as CreditType;
      typeStats[type].leads++;
      if (session.status === "completed") {
        typeStats[type].completed++;
      }
    });

    const conversionByType = (Object.entries(typeStats) as [CreditType, { leads: number; completed: number }][])
      .map(([type, data]) => ({
        type,
        leads: data.leads,
        completed: data.completed,
        rate: data.leads > 0 ? (data.completed / data.leads) * 100 : 0,
      }))
      .filter((item) => item.leads > 0);

    // Activity data (last 30 days)
    const activityData = generateActivityData(sessions);

    return {
      totalOffers: allOffers.length,
      conversionRate,
      topBanks,
      conversionByType,
      activityData,
    };
  }, [sessions, completedSessions]);

  const isLoading = leadsLoading || sessionsLoading;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Witaj, {advisor?.name?.split(" ")[0] || "Doradco"}!
        </h1>
        <p className="text-gray-600 mt-1">Oto przegląd Twojej aktywności</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Aktywne sesje"
          value={activeSessions.length}
          icon={<MessageSquare className="w-5 h-5" />}
          iconBgColor="bg-primary-100"
          iconColor="text-primary-600"
          trend={
            sessions.length > 0
              ? {
                  direction: activeSessions.length > 0 ? "up" : "stable",
                  value: `${activeSessions.length} z ${sessions.length}`,
                }
              : undefined
          }
          isLoading={isLoading}
        />

        <StatCard
          title="Konwersja"
          value={`${stats.conversionRate}%`}
          icon={<TrendingUp className="w-5 h-5" />}
          iconBgColor="bg-success-100"
          iconColor="text-success-600"
          trend={
            completedSessions.length > 0
              ? {
                  direction: stats.conversionRate >= 40 ? "up" : stats.conversionRate >= 20 ? "stable" : "down",
                  value: `${completedSessions.length} ukończonych`,
                }
              : undefined
          }
          isLoading={isLoading}
        />

        <StatCard
          title="Oferty"
          value={stats.totalOffers}
          icon={<FileText className="w-5 h-5" />}
          iconBgColor="bg-info-100"
          iconColor="text-info-600"
          trend={
            stats.totalOffers > 0
              ? {
                  direction: "up",
                  value: `${(stats.totalOffers / Math.max(sessions.length, 1)).toFixed(1)} śr./sesję`,
                }
              : undefined
          }
          isLoading={isLoading}
        />

        <StatCard
          title="Twoje kredyty"
          value={advisor?.credits || 0}
          icon={<CreditCard className="w-5 h-5" />}
          iconBgColor="bg-warning-100"
          iconColor="text-warning-600"
          isLoading={false}
        />
      </div>

      {/* Activity Chart */}
      <ActivityChart data={stats.activityData} isLoading={isLoading} />

      {/* Bottom Grid: Top Banks & Conversion by Type */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TopBanksList banks={stats.topBanks} isLoading={isLoading} />
        <ConversionBars data={stats.conversionByType} isLoading={isLoading} />
      </div>

      {/* Quick Access Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Available Leads */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Nowe leady
            </CardTitle>
            <Link to="/leads">
              <Button variant="ghost" size="sm" rightIcon={<ArrowRight className="w-4 h-4" />}>
                Zobacz wszystkie
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {leadsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : leads.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Brak dostępnych leadów</p>
            ) : (
              <div className="space-y-3">
                {leads.slice(0, 5).map((lead) => (
                  <div
                    key={lead.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{lead.name}</p>
                      <p className="text-sm text-gray-600">{formatCurrency(lead.amount)}</p>
                    </div>
                    <Badge variant={getStatusVariant(lead.status)}>
                      {getStatusLabel(lead.status)}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Active Sessions */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Aktywne sesje
            </CardTitle>
            <Link to="/sessions">
              <Button variant="ghost" size="sm" rightIcon={<ArrowRight className="w-4 h-4" />}>
                Zobacz wszystkie
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {sessionsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : activeSessions.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Brak aktywnych sesji</p>
            ) : (
              <div className="space-y-3">
                {activeSessions.slice(0, 5).map((session) => (
                  <Link
                    key={session.id}
                    to={`/sessions/${session.id}`}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div>
                      <p className="font-medium text-gray-900">
                        {session.clientData?.name || "Klient"}
                      </p>
                      <p className="text-sm text-gray-600">{formatDateTime(session.createdAt)}</p>
                    </div>
                    <Badge variant={getStatusVariant(session.status)}>
                      {getStatusLabel(session.status)}
                    </Badge>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/**
 * Generate activity data from sessions for the chart
 * Groups sessions and offers by date for the last 30 days
 */
function generateActivityData(sessions: Session[]) {
  const data: Record<string, { sessions: number; offers: number }> = {};

  // Initialize last 30 days
  for (let i = 29; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    const dateStr = date.toISOString().split("T")[0];
    data[dateStr] = { sessions: 0, offers: 0 };
  }

  // Count sessions and offers by date
  sessions.forEach((session) => {
    const dateStr = session.createdAt?.split("T")[0];
    if (dateStr && data[dateStr]) {
      data[dateStr].sessions++;
      data[dateStr].offers += (session.offers?.length || 0);
    }
  });

  // Convert to array
  return Object.entries(data).map(([date, counts]) => ({
    date,
    sessions: counts.sessions,
    offers: counts.offers,
  }));
}
